import { getStoryBySlug } from '@cinch-nx/data-storyblok'
import { Env, readFromEnv } from '@cinch-nx/environments'
import {
  generateStoreNameWithType,
  generateStoreType,
} from '@cinch-nx/landing-ui-shared'
import { setTag, tagError, traceFunction } from '@core/datadog-server'
import { GetStaticPropsContext } from 'next'
import {
  StoreInformationPageStoryContent,
  StoryBlokAddress,
} from './types/store-page'

export interface GetStaticPropsGeneratorArgs {
  preview: boolean
}

export const VEHICLE_SEARCH_URL = `${readFromEnv(
  Env.SearchServiceUrl,
)}/vehicles`

const formatHeroAddress = (address: StoryBlokAddress) =>
  `${address.addressLine1}, ${address.addressLine2}, ${address.townOrCity}, ${address.postCode}`

export const getStaticPropsfn = async ({
  params,
  preview = false,
}: GetStaticPropsContext) => {
  const storeName = params?.slug as string

  try {
    const story = await getStoryBySlug<StoreInformationPageStoryContent>(
      `store/${storeName}`,
      {
        version: preview ? 'draft' : 'published',
      },
    )

    if (!story) {
      throw new Error('Store information story not found')
    }

    let storeVehicles = []

    try {
      const response = await fetch(
        `${readFromEnv(Env.SearchServiceUrl)}/vehicles?store=${storeName}`,
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch vehicles: ${response.statusText}`)
      }

      const { vehicleListings } = await response.json()

      storeVehicles =
        vehicleListings.length < 9
          ? vehicleListings
          : vehicleListings.slice(0, 9)
    } catch (error) {
      if (error instanceof Error || typeof error === 'string') {
        tagError(error)
      }
      storeVehicles = []
    }
    setTag('response.code', 200)
    setTag('page.rendered', 'ISR')
    setTag('page.revalidate-in', '60s')
    setTag('page.name', `store/${storeName}`)
    setTag(
      'page.params',
      JSON.stringify(
        {
          preview,
          storeVehicles,
        },
        null,
        2,
      ),
    )
    setTag('page.response-code', '200')

    const storeType = generateStoreType(storeName)
    const heroTitle = story.content.storeHero[0].title
    const title = heroTitle
      ? heroTitle
      : generateStoreNameWithType(storeName, true)

    const heroProps = {
      ...story.content.storeHero[0],

      title,
      address: formatHeroAddress(story.content.address[0]),
      telephoneNumber: story.content.phoneNumbers[0].phoneNumber,
    }

    return {
      props: {
        story,
        storeType,
        preview,
        generatedAt: Date.now(),
        layoutProps: {
          showBorderRadius: true,
          heroProps,
        },
        storeVehicles,
      },
      revalidate: 60, // In seconds
    }
  } catch (error) {
    if (typeof error === 'string' || error instanceof Error) {
      console.log(error)
    }

    return {
      notFound: true,
    }
  }
}

export type LandingFeatureStoreProps = Extract<
  Awaited<ReturnType<typeof getStaticPropsfn>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { props: any }
>['props']

export const getStaticProps = traceFunction(
  'store-page.getStaticProps',
  getStaticPropsfn,
)
